import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Container from 'react-bootstrap/Container';
import Dropdown from 'react-bootstrap/Dropdown';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Table from 'react-bootstrap/Table';
import { getCaseStudyPublishValidation, hasManualStageProgression, hasStagedLiveCase, normalizeCaseStudy, QUESTION_DISCIPLINES } from '../../lib/caseStudy';
import { api } from '../../lib/api';

// Per-discipline badge styling. Mirrors the question editor so colours
// stay consistent: Medical = danger, Pharmacy = info, Nursing = success.
const disciplineLabels = {
  medical: 'Medical',
  pharmacy: 'Pharmacy',
  nursing: 'Nursing',
};
const disciplineBadgeBg = {
  medical: 'danger',
  pharmacy: 'info',
  nursing: 'success',
};

// The case body (questions + disciplineMode) lives on draftData when the
// row comes back from /api/case-studies/workspace; on published cases it
// is also mirrored on publishedData. Falls back to top-level fields too
// in case the row was already normalised by an upstream call.
function getCaseBody(caseStudy) {
  return caseStudy?.draftData || caseStudy?.publishedData || caseStudy || {};
}

function isCaseInDisciplineMode(caseStudy) {
  return Boolean(getCaseBody(caseStudy).disciplineMode);
}

// Returns the unique disciplines used by any question on this case (its
// actual coverage). When no question is tagged but the case is in
// discipline mode, returns an empty array (caller will show 'All
// disciplines' as the safe default).
function getCaseDisciplineCoverage(caseStudy) {
  const questions = Array.isArray(getCaseBody(caseStudy).questions) ? getCaseBody(caseStudy).questions : [];
  const seen = new Set();
  for (const q of questions) {
    const tags = Array.isArray(q?.disciplines) ? q.disciplines : [];
    for (const t of tags) {
      if (QUESTION_DISCIPLINES.includes(t)) seen.add(t);
    }
  }
  return Array.from(seen);
}

const formatDateTime = (value) => {
  if (!value) {
    return 'Not yet';
  }
  try {
    return new Date(value).toLocaleString('en-GB');
  } catch (_error) {
    return value;
  }
};

const getStatusMeta = (caseStudy) => {
  if (caseStudy.status === 'live_classroom') {
    return { label: 'Live classroom', badge: 'warning', availability: 'Join code', availabilityBadge: 'warning' };
  }

  if (caseStudy.status === 'pending_approval') {
    return { label: 'Pending approval', badge: 'info', availability: 'Awaiting admin', availabilityBadge: 'info' };
  }

  if (caseStudy.status === 'self_paced' || caseStudy.studentAccessEnabled) {
    return { label: 'Case Study Published', badge: 'primary', availability: 'Student access on', availabilityBadge: 'success' };
  }

  if (caseStudy.status === 'archived') {
    return { label: 'Archived', badge: 'secondary', availability: 'Students off', availabilityBadge: 'danger' };
  }

  if (caseStudy.status === 'closed') {
    return { label: 'Closed', badge: 'secondary', availability: 'Students off', availabilityBadge: 'danger' };
  }

  return { label: 'Draft', badge: 'secondary', availability: 'Not Published', availabilityBadge: 'danger' };
};

const getCaseStudyDraft = (caseStudy) => normalizeCaseStudy({
  ...(caseStudy?.draftData || {}),
  id: caseStudy?.id || '',
});

const getStudentShareSummary = (caseStudy) => {
  const studentShares = (caseStudy.shares || []).filter((share) => share.shareType === 'student');
  if (!studentShares.length) {
    return 'Not directly shared with students.';
  }

  const names = studentShares
    .slice(0, 3)
    .map((share) => share.recipientName || share.recipientEmail)
    .filter(Boolean);
  const extraCount = studentShares.length - names.length;
  return `Shared with ${names.join(', ')}${extraCount > 0 ? ` and ${extraCount} more` : ''}.`;
};

const getLifecycleBadge = (caseStudy) => {
  if (caseStudy.status === 'live_classroom') return { className: 'case-lifecycle-badge--live', icon: 'bi-broadcast-pin', label: 'Live classroom' };
  if (caseStudy.status === 'self_paced' || caseStudy.studentAccessEnabled) return { className: 'case-lifecycle-badge--live', icon: 'bi-circle-fill', label: 'Live to students' };
  if (caseStudy.status === 'pending_approval') return { className: 'case-lifecycle-badge--pending', icon: 'bi-hourglass-split', label: 'Pending approval' };
  if (caseStudy.status === 'archived') return { className: 'case-lifecycle-badge--archived', icon: 'bi-archive', label: 'Archived' };
  if (caseStudy.status === 'closed') return { className: 'case-lifecycle-badge--closed', icon: 'bi-slash-circle', label: 'Closed' };
  return { className: 'case-lifecycle-badge--draft', icon: 'bi-circle', label: 'Not published' };
};

const CaseStudyStatusCell = ({ caseStudy, canApprove, onApproveCase, onRejectCase, onOpenReviewThread, reviewCommentCount, canCurateFreeTier, onToggleFreeTier }) => {
  const lifecycle = getLifecycleBadge(caseStudy);
  const isPending = caseStudy.status === 'pending_approval';
  const isLivePresentation = caseStudy.status === 'live_classroom';

  return (
    <div className="facilitator-library-status text-center">
      <div className="d-flex flex-column align-items-center gap-2">
        <span className={`case-lifecycle-badge ${lifecycle.className}`}>
          <i className={`bi ${lifecycle.icon} me-1`} aria-hidden="true" />
          {lifecycle.label}
        </span>
        {canCurateFreeTier ? (
          <CaseStudyTierCell caseStudy={caseStudy} onToggleFreeTier={onToggleFreeTier} />
        ) : null}
        {isCaseInDisciplineMode(caseStudy) ? (() => {
          const coverage = getCaseDisciplineCoverage(caseStudy);
          if (!coverage.length) {
            return <Badge bg="light" text="dark" title="Discipline mode is on but no questions are tagged yet — every student sees every question.">All disciplines</Badge>;
          }
          return (
            <div className="d-flex flex-wrap gap-1 justify-content-center">
              {coverage.map((d) => (
                <Badge key={`coverage-${d}`} bg={disciplineBadgeBg[d] || 'secondary'} title={`At least one question is tagged for ${disciplineLabels[d]} students.`}>
                  {disciplineLabels[d] || d}
                </Badge>
              ))}
            </div>
          );
        })() : null}
        {isPending && canApprove ? (
          <div className="d-flex gap-2 justify-content-center flex-wrap">
            <Button type="button" size="sm" variant="success" onClick={() => onApproveCase?.(caseStudy)}>
              <i className="bi bi-check-circle me-1" /> Approve
            </Button>
            <Button type="button" size="sm" variant="outline-danger" onClick={() => onRejectCase?.(caseStudy)}>
              <i className="bi bi-x-circle me-1" /> Reject
            </Button>
          </div>
        ) : null}
        {reviewCommentCount > 0 ? (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="p-0"
            onClick={() => onOpenReviewThread?.(caseStudy)}
          >
            <i className="bi bi-chat-left-text me-1" />
            Review thread ({reviewCommentCount})
          </Button>
        ) : null}
        {isLivePresentation && caseStudy.activeSessionCode ? (
          <div className="small text-muted">
            Live code <strong>{caseStudy.activeSessionCode}</strong>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const CaseStudyTierCell = ({ caseStudy, onToggleFreeTier }) => {
  const isFreeTier = Boolean(caseStudy.freeTierEligible);
  return (
    <Button
      type="button"
      size="sm"
      variant={isFreeTier ? 'success' : 'outline-secondary'}
      className="facilitator-library-status-pill"
      title={isFreeTier ? 'Click to require subscription for this case' : 'Click to make this case free for non-paying students'}
      onClick={() => onToggleFreeTier?.(caseStudy.id, !isFreeTier)}
    >
      <i className={`bi ${isFreeTier ? 'bi-unlock-fill' : 'bi-lock-fill'} me-1`} aria-hidden="true" />
      {isFreeTier ? 'Free' : 'Paid'}
    </Button>
  );
};

const ActionIconButton = ({ label, icon, variant, onClick }) => (
  <Button
    type="button"
    size="sm"
    variant={variant}
    className="facilitator-library-action-button"
    onClick={onClick}
    title={label}
    aria-label={label}
  >
    <i className={`bi ${icon}`} aria-hidden="true" />
  </Button>
);

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending_approval', label: 'Pending approval' },
  { value: 'live_classroom', label: 'Live presentation' },
  { value: 'self_paced', label: 'Case study' },
  { value: 'closed', label: 'Closed' },
  { value: 'archived', label: 'Archived' },
];

const PAGE_SIZE = 20;

// Return black or white depending on which has better contrast against the
// supplied hex colour. Used to keep the banner header readable when the
// tenant brand colour is pale.
const readableTextOn = (hex) => {
  if (typeof hex !== 'string') return '#ffffff';
  const clean = hex.replace('#', '');
  const value = clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean;
  if (value.length !== 6) return '#ffffff';
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  // Relative luminance per WCAG. Threshold of 0.5 gives a safe split.
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#111827' : '#ffffff';
};

const includesSearchText = (value, searchTerm) => String(value || '').toLowerCase().includes(searchTerm);

const getPageCount = (items) => Math.max(1, Math.ceil(items.length / PAGE_SIZE));

const FacilitatorCaseLibrary = ({
  ownedCaseStudies = [],
  caseStudySets = [],
  onBack,
  onCreateCaseStudy,
  onEditCaseStudy,
  onViewResults,
  onDeleteCaseStudy,
  onArchiveCaseStudy,
  onUnarchiveCaseStudy,
  onScheduleCaseStudyPublish,
  onToggleStudentAccess,
  onEndLiveClassroom,
  onToggleFreeTier,
  onApproveCase,
  onRejectCase,
  user,
  tenant,
  onTestCaseStudy,
  onPublishCaseStudy,
  onPresentCaseStudy,
  onCopyPresentationLink,
  onShareCaseStudy,
  onRevokeCaseStudyShare,
  onCopyStudentLink,
  onCreateCaseStudySet,
  onUpdateCaseStudySet,
  onDeleteCaseStudySet,
  onShareCaseStudySet,
  onRefreshCaseStudies,
  token,
}) => {
  // Show the free-tier curator toggle only on the central students tenant,
  // and only to users who can change the flag (platform admin or that
  // tenant's educator-admin). The backend enforces the same rule.
  const canCurateFreeTier = Boolean(
    tenant?.tenantType === 'central_students'
    && (user?.role === 'platform_admin' || user?.role === 'educator_admin')
    && typeof onToggleFreeTier === 'function'
  );

  // educator_admin or platform_admin in this tenant can approve / reject
  // pending cases. Backend enforces the same rule.
  const canApprove = Boolean(
    (user?.role === 'platform_admin' || user?.role === 'educator_admin')
    && typeof onApproveCase === 'function'
    && typeof onRejectCase === 'function'
  );

  const filterControlsRef = useRef(null);
  const caseTableRef = useRef(null);
  const firstActionsRef = useRef(null);
  const firstPublishRef = useRef(null);
  const setsSectionRef = useRef(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [shareType, setShareType] = useState('student');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [historyLoadingId, setHistoryLoadingId] = useState('');
  const [sharesTarget, setSharesTarget] = useState(null);
  const [scheduleTarget, setScheduleTarget] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({ publishAt: '', unpublishAt: '' });
  const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
  const [scheduleError, setScheduleError] = useState('');
  const [visibilityTarget, setVisibilityTarget] = useState(null);
  const [visibilityCohorts, setVisibilityCohorts] = useState([]);
  const [visibilitySelectedIds, setVisibilitySelectedIds] = useState([]);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [visibilitySaving, setVisibilitySaving] = useState(false);
  const [visibilityError, setVisibilityError] = useState('');
  const [visibilityCounts, setVisibilityCounts] = useState({});
  const [reviewTarget, setReviewTarget] = useState(null);
  const [reviewMode, setReviewMode] = useState('comment');
  const [reviewComments, setReviewComments] = useState([]);
  const [reviewBody, setReviewBody] = useState('');
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [reviewError, setReviewError] = useState('');
  const [reviewCounts, setReviewCounts] = useState({});
  const [caseSearch, setCaseSearch] = useState('');
  const [casePage, setCasePage] = useState(1);
  const [setSearch, setSetSearch] = useState('');
  const [setPage, setSetPage] = useState(1);
  const [shareEmail, setShareEmail] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tierFilter, setTierFilter] = useState('all'); // 'all' | 'free' | 'paid'
  const [showFilters, setShowFilters] = useState(false);
  const [sortKey, setSortKey] = useState('title'); // 'title' | 'updatedAt'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((current) => (current === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'updatedAt' ? 'desc' : 'asc');
    }
  };

  const [shareSubmitting, setShareSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [revokeSubmitting, setRevokeSubmitting] = useState('');
  const [shareError, setShareError] = useState('');
  const [setEditorTarget, setSetEditorTarget] = useState(null);
  const [showSetEditor, setShowSetEditor] = useState(false);
  const [setFields, setSetFields] = useState({ title: '', description: '', caseStudyIds: [] });
  const [setSubmitting, setSetSubmitting] = useState(false);
  const [caseStudySetDeleteTarget, setCaseStudySetDeleteTarget] = useState(null);
  const [caseStudySetShareTarget, setCaseStudySetShareTarget] = useState(null);
  const [caseStudySetShareEmail, setCaseStudySetShareEmail] = useState('');
  const [caseStudySetShareSubmitting, setCaseStudySetShareSubmitting] = useState(false);
  const [publishTarget, setPublishTarget] = useState(null);
  const [publishSubmitting, setPublishSubmitting] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialCardStyle, setTutorialCardStyle] = useState({});

  const totals = useMemo(() => ({
    owned: ownedCaseStudies.length,
  }), [ownedCaseStudies.length]);
  const sortedOwnedCaseStudies = useMemo(() => {
    const sign = sortDir === 'desc' ? -1 : 1;
    return [...ownedCaseStudies].sort((left, right) => {
      if (sortKey === 'updatedAt') {
        const ld = left.updatedAt ? new Date(left.updatedAt).getTime() : 0;
        const rd = right.updatedAt ? new Date(right.updatedAt).getTime() : 0;
        return sign * (ld - rd);
      }
      return sign * String(left.title || '').localeCompare(String(right.title || ''), 'en-GB', { sensitivity: 'base' });
    });
  }, [ownedCaseStudies, sortKey, sortDir]);
  const caseSearchTerm = caseSearch.trim().toLowerCase();
  const filteredOwnedCaseStudies = useMemo(() => {
    return sortedOwnedCaseStudies.filter((caseStudy) => {
      const matchesStatus = (() => {
        if (statusFilter === 'all') {
          return true;
        }
        if (statusFilter === 'self_paced') {
          return caseStudy.status === 'self_paced' || caseStudy.studentAccessEnabled;
        }
        return caseStudy.status === statusFilter;
      })();

      if (!matchesStatus) {
        return false;
      }

      if (tierFilter === 'free' && !caseStudy.freeTierEligible) return false;
      if (tierFilter === 'paid' && caseStudy.freeTierEligible) return false;

      if (!caseSearchTerm) {
        return true;
      }

      return [
        caseStudy.title,
        caseStudy.draftData?.short_description,
        caseStudy.summary,
        caseStudy.activeSessionCode,
      ].some((value) => includesSearchText(value, caseSearchTerm));
    });
  }, [sortedOwnedCaseStudies, statusFilter, tierFilter, caseSearchTerm]);
  const casePageCount = useMemo(() => getPageCount(filteredOwnedCaseStudies), [filteredOwnedCaseStudies]);
  const pagedOwnedCaseStudies = useMemo(() => {
    const startIndex = (casePage - 1) * PAGE_SIZE;
    return filteredOwnedCaseStudies.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredOwnedCaseStudies, casePage]);
  const sortedCaseStudySets = useMemo(
    () => [...caseStudySets].sort((left, right) => String(left.title || '').localeCompare(String(right.title || ''), 'en-GB', { sensitivity: 'base' })),
    [caseStudySets]
  );
  const setSearchTerm = setSearch.trim().toLowerCase();
  const filteredCaseStudySets = useMemo(() => (
    sortedCaseStudySets.filter((caseStudySet) => {
      if (!setSearchTerm) {
        return true;
      }

      return [
        caseStudySet.title,
        caseStudySet.description,
        ...(caseStudySet.caseStudies || []).map((item) => item.title),
      ].some((value) => includesSearchText(value, setSearchTerm));
    })
  ), [sortedCaseStudySets, setSearchTerm]);
  const setPageCount = useMemo(() => getPageCount(filteredCaseStudySets), [filteredCaseStudySets]);
  const pagedCaseStudySets = useMemo(() => {
    const startIndex = (setPage - 1) * PAGE_SIZE;
    return filteredCaseStudySets.slice(startIndex, startIndex + PAGE_SIZE);
  }, [filteredCaseStudySets, setPage]);
  const eligibleSetCaseStudies = useMemo(
    () => sortedOwnedCaseStudies.filter((caseStudy) => caseStudy.status !== 'live_classroom'),
    [sortedOwnedCaseStudies]
  );
  const tutorialSteps = useMemo(() => ([
    {
      key: 'filters',
      title: 'Filter and find cases',
      body: 'Use status filters and search to narrow the library by title, description, or live classroom code.',
      ref: filterControlsRef,
    },
    {
      key: 'cases',
      title: 'Review case status',
      body: 'Each row shows the case name, current publication status, student availability, recent activity, and last updated date.',
      ref: caseTableRef,
    },
    {
      key: 'actions',
      title: 'Manage each case',
      body: 'Use these actions to edit, review results, share, manage student shares, present live cases, copy links, archive, or delete.',
      ref: firstActionsRef,
    },
    {
      key: 'publish',
      title: 'Publish or present',
      body: 'Start a live classroom for real-time teaching, or publish a self-paced case study for students to complete independently.',
      ref: firstPublishRef,
    },
    {
      key: 'sets',
      title: 'Group cases into sets',
      body: 'Case study sets let you bundle related non-live cases and share the whole group with students.',
      ref: setsSectionRef,
    },
  ]), []);

  React.useEffect(() => {
    setCasePage(1);
  }, [statusFilter, tierFilter, caseSearch]);

  React.useEffect(() => {
    if (casePage > casePageCount) {
      setCasePage(casePageCount);
    }
  }, [casePage, casePageCount]);

  React.useEffect(() => {
    setSetPage(1);
  }, [setSearch]);

  React.useEffect(() => {
    if (setPage > setPageCount) {
      setSetPage(setPageCount);
    }
  }, [setPage, setPageCount]);

  useEffect(() => {
    if (!showTutorial) {
      return;
    }

    const targetNode = tutorialSteps[tutorialStep]?.ref?.current;
    if (targetNode?.scrollIntoView) {
      targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [showTutorial, tutorialStep, tutorialSteps]);

  useEffect(() => {
    if (!showTutorial) {
      setTutorialCardStyle({});
      return undefined;
    }

    const updateTutorialCardPosition = () => {
      const targetNode = tutorialSteps[tutorialStep]?.ref?.current;
      if (!targetNode) {
        setTutorialCardStyle({
          width: 'min(420px, calc(100vw - 2rem))',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        });
        return;
      }

      const rect = targetNode.getBoundingClientRect();
      const cardWidth = Math.min(420, window.innerWidth - 32);
      const gutter = 16;
      const canPlaceRight = rect.right + gutter + cardWidth <= window.innerWidth - 16;
      const canPlaceLeft = rect.left - gutter - cardWidth >= 16;
      let left;
      let top;

      if (canPlaceRight) {
        left = rect.right + gutter;
        top = rect.top;
      } else if (canPlaceLeft) {
        left = rect.left - gutter - cardWidth;
        top = rect.top;
      } else {
        left = Math.max(16, Math.min(rect.left, window.innerWidth - cardWidth - 16));
        top = rect.bottom + gutter;
        if (top + 240 > window.innerHeight) {
          top = Math.max(16, rect.top - 240 - gutter);
        }
      }

      setTutorialCardStyle({
        width: `${cardWidth}px`,
        top: `${Math.max(16, top)}px`,
        left: `${Math.max(16, left)}px`,
        transform: 'none',
      });
    };

    updateTutorialCardPosition();
    window.addEventListener('resize', updateTutorialCardPosition);
    window.addEventListener('scroll', updateTutorialCardPosition, true);
    return () => {
      window.removeEventListener('resize', updateTutorialCardPosition);
      window.removeEventListener('scroll', updateTutorialCardPosition, true);
    };
  }, [showTutorial, tutorialStep, tutorialSteps]);

  const closeTutorial = () => {
    setShowTutorial(false);
    setTutorialStep(0);
  };

  const isTutorialTargetActive = (key) => showTutorial && tutorialSteps[tutorialStep]?.key === key;

  const openShareModal = (caseStudy) => {
    setShareTarget(caseStudy);
    setShareEmail('');
    setShareType('student');
    setShareError('');
  };

  const closeShareModal = () => {
    setShareTarget(null);
    setShareEmail('');
    setShareType('student');
    setShareSubmitting(false);
    setShareError('');
  };

  const openDeleteModal = (caseStudy) => {
    setDeleteTarget(caseStudy);
    setDeleteSubmitting(false);
  };

  const closeDeleteModal = () => {
    setDeleteTarget(null);
    setDeleteSubmitting(false);
  };

  const openHistoryModal = async (caseStudy) => {
    setHistoryLoadingId(caseStudy.id);
    try {
      const refreshed = await onRefreshCaseStudies?.(caseStudy.id);
      const refreshedCase = (refreshed?.ownedCaseStudies || []).find((item) => item.id === caseStudy.id);
      setHistoryTarget(refreshedCase || caseStudy);
    } finally {
      setHistoryLoadingId('');
    }
  };

  const closeHistoryModal = () => {
    setHistoryTarget(null);
  };

  const openSharesModal = (caseStudy) => {
    setSharesTarget(caseStudy);
    setRevokeSubmitting('');
  };

  const closeSharesModal = () => {
    setSharesTarget(null);
    setRevokeSubmitting('');
  };

  const submitShare = async (event) => {
    event.preventDefault();
    if (!shareTarget) {
      return;
    }
    setShareSubmitting(true);
    setShareError('');
    try {
      await onShareCaseStudy(shareTarget.id, shareEmail.trim(), shareType);
      closeShareModal();
    } catch (error) {
      setShareError(error.message || 'Unable to share this case study.');
      setShareSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) {
      return;
    }
    setDeleteSubmitting(true);
    try {
      await onDeleteCaseStudy(deleteTarget.id);
      closeDeleteModal();
    } catch (_error) {
      setDeleteSubmitting(false);
    }
  };

  const revokeShare = async (caseStudyId, shareId) => {
    if (!onRevokeCaseStudyShare) {
      return;
    }
    setRevokeSubmitting(shareId);
    try {
      await onRevokeCaseStudyShare(caseStudyId, shareId);
    } finally {
      setRevokeSubmitting('');
    }
  };

  const toLocalInputValue = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openScheduleModal = (caseStudy) => {
    setScheduleTarget(caseStudy);
    setScheduleForm({
      publishAt: toLocalInputValue(caseStudy.scheduledPublishAt),
      unpublishAt: toLocalInputValue(caseStudy.scheduledUnpublishAt),
    });
    setScheduleError('');
  };

  const closeScheduleModal = () => {
    setScheduleTarget(null);
    setScheduleForm({ publishAt: '', unpublishAt: '' });
    setScheduleSubmitting(false);
    setScheduleError('');
  };

  const submitSchedule = async (event) => {
    event.preventDefault();
    if (!scheduleTarget || !onScheduleCaseStudyPublish) return;
    setScheduleSubmitting(true);
    setScheduleError('');
    try {
      await onScheduleCaseStudyPublish(scheduleTarget.id, {
        scheduledPublishAt: scheduleForm.publishAt ? new Date(scheduleForm.publishAt).toISOString() : null,
        scheduledUnpublishAt: scheduleForm.unpublishAt ? new Date(scheduleForm.unpublishAt).toISOString() : null,
      });
      closeScheduleModal();
    } catch (error) {
      setScheduleError(error?.payload?.error || error.message || 'Unable to update the schedule.');
      setScheduleSubmitting(false);
    }
  };

  const clearSchedule = async () => {
    if (!scheduleTarget || !onScheduleCaseStudyPublish) return;
    setScheduleSubmitting(true);
    setScheduleError('');
    try {
      await onScheduleCaseStudyPublish(scheduleTarget.id, { scheduledPublishAt: null, scheduledUnpublishAt: null });
      closeScheduleModal();
    } catch (error) {
      setScheduleError(error?.payload?.error || error.message || 'Unable to clear the schedule.');
      setScheduleSubmitting(false);
    }
  };

  const openVisibilityModal = async (caseStudy) => {
    setVisibilityTarget(caseStudy);
    setVisibilitySelectedIds([]);
    setVisibilityCohorts([]);
    setVisibilityError('');
    setVisibilityLoading(true);
    try {
      const [cohortsResult, visibilityResult] = await Promise.all([
        api.listCohorts(token),
        api.getCaseStudyCohortVisibility(caseStudy.id, token),
      ]);
      setVisibilityCohorts(Array.isArray(cohortsResult?.cohorts) ? cohortsResult.cohorts : []);
      setVisibilitySelectedIds(Array.isArray(visibilityResult?.cohortIds) ? visibilityResult.cohortIds : []);
    } catch (error) {
      setVisibilityError(error?.payload?.error || error.message || 'Unable to load visibility settings.');
    } finally {
      setVisibilityLoading(false);
    }
  };

  const closeVisibilityModal = () => {
    if (visibilitySaving) return;
    setVisibilityTarget(null);
    setVisibilityCohorts([]);
    setVisibilitySelectedIds([]);
    setVisibilityError('');
  };

  const toggleVisibilitySelection = (cohortId) => {
    setVisibilitySelectedIds((current) => (
      current.includes(cohortId)
        ? current.filter((id) => id !== cohortId)
        : [...current, cohortId]
    ));
  };

  const submitVisibility = async (event) => {
    event.preventDefault();
    if (!visibilityTarget) return;
    setVisibilitySaving(true);
    setVisibilityError('');
    try {
      await api.setCaseStudyCohortVisibility(visibilityTarget.id, visibilitySelectedIds, token);
      setVisibilityCounts((current) => ({ ...current, [visibilityTarget.id]: visibilitySelectedIds.length }));
      setVisibilityTarget(null);
      setVisibilityCohorts([]);
      setVisibilitySelectedIds([]);
    } catch (error) {
      setVisibilityError(error?.payload?.error || error.message || 'Unable to save visibility.');
    } finally {
      setVisibilitySaving(false);
    }
  };

  const cohortVisibilityBadgeCount = (caseStudy) => {
    if (Object.prototype.hasOwnProperty.call(visibilityCounts, caseStudy.id)) {
      return visibilityCounts[caseStudy.id];
    }
    return caseStudy.cohortVisibilityCount || 0;
  };

  const reviewCommentBadgeCount = (caseStudy) => {
    if (Object.prototype.hasOwnProperty.call(reviewCounts, caseStudy.id)) {
      return reviewCounts[caseStudy.id];
    }
    return caseStudy.reviewCommentCount || 0;
  };

  const openReviewModal = async (caseStudy, mode = 'comment') => {
    setReviewTarget(caseStudy);
    setReviewMode(mode);
    setReviewBody('');
    setReviewError('');
    setReviewComments([]);
    setReviewLoading(true);
    try {
      const result = await api.listCaseStudyReviewComments(caseStudy.id, token);
      const comments = Array.isArray(result?.comments) ? result.comments : [];
      setReviewComments(comments);
      setReviewCounts((current) => ({ ...current, [caseStudy.id]: comments.length }));
    } catch (error) {
      setReviewError(error?.payload?.error || error.message || 'Unable to load review thread.');
    } finally {
      setReviewLoading(false);
    }
  };

  const closeReviewModal = () => {
    if (reviewSubmitting) return;
    setReviewTarget(null);
    setReviewComments([]);
    setReviewBody('');
    setReviewError('');
    setReviewMode('comment');
  };

  const submitReviewComment = async (event) => {
    event.preventDefault();
    if (!reviewTarget) return;
    const body = reviewBody.trim();
    if (!body) {
      setReviewError('Type a comment first.');
      return;
    }
    setReviewSubmitting(true);
    setReviewError('');
    try {
      const result = await api.postCaseStudyReviewComment(reviewTarget.id, body, token);
      const next = [...reviewComments, result.comment];
      setReviewComments(next);
      setReviewCounts((current) => ({ ...current, [reviewTarget.id]: next.length }));
      setReviewBody('');
    } catch (error) {
      setReviewError(error?.payload?.error || error.message || 'Unable to post comment.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const submitReviewReject = async () => {
    if (!reviewTarget) return;
    const body = reviewBody.trim();
    if (!body) {
      setReviewError('A rejection reason is required so the author knows what to change.');
      return;
    }
    setReviewSubmitting(true);
    setReviewError('');
    try {
      await onRejectCase?.(reviewTarget, body);
      // Re-load thread to pick up the new rejection row.
      const refreshed = await api.listCaseStudyReviewComments(reviewTarget.id, token);
      const comments = Array.isArray(refreshed?.comments) ? refreshed.comments : [];
      setReviewComments(comments);
      setReviewCounts((current) => ({ ...current, [reviewTarget.id]: comments.length }));
      setReviewBody('');
      setReviewMode('comment');
    } catch (error) {
      setReviewError(error?.payload?.error || error.message || 'Unable to reject case.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const submitReviewApprove = async () => {
    if (!reviewTarget) return;
    const body = reviewBody.trim();
    setReviewSubmitting(true);
    setReviewError('');
    try {
      await onApproveCase?.(reviewTarget, body);
      const refreshed = await api.listCaseStudyReviewComments(reviewTarget.id, token);
      const comments = Array.isArray(refreshed?.comments) ? refreshed.comments : [];
      setReviewComments(comments);
      setReviewCounts((current) => ({ ...current, [reviewTarget.id]: comments.length }));
      setReviewBody('');
      setReviewMode('comment');
    } catch (error) {
      setReviewError(error?.payload?.error || error.message || 'Unable to approve case.');
    } finally {
      setReviewSubmitting(false);
    }
  };

  const deleteReviewComment = async (commentId) => {
    if (!reviewTarget) return;
    if (!window.confirm('Delete this comment?')) return;
    try {
      await api.deleteCaseStudyReviewComment(reviewTarget.id, commentId, token);
      const next = reviewComments.filter((c) => c.id !== commentId);
      setReviewComments(next);
      setReviewCounts((current) => ({ ...current, [reviewTarget.id]: next.length }));
    } catch (error) {
      setReviewError(error?.payload?.error || error.message || 'Unable to delete comment.');
    }
  };

  const openSetEditor = (caseStudySet = null) => {
    setSetEditorTarget(caseStudySet);
    setSetFields({
      title: caseStudySet?.title || '',
      description: caseStudySet?.description || '',
      caseStudyIds: (caseStudySet?.caseStudies || []).map((item) => item.caseStudyId),
    });
    setShowSetEditor(true);
  };

  const closeSetEditor = () => {
    setShowSetEditor(false);
    setSetEditorTarget(null);
    setSetFields({ title: '', description: '', caseStudyIds: [] });
    setSetSubmitting(false);
  };

  const submitSetEditor = async (event) => {
    event?.preventDefault?.();
    setSetSubmitting(true);
    try {
      const payload = {
        title: setFields.title.trim(),
        description: setFields.description.trim(),
        caseStudyIds: setFields.caseStudyIds,
      };
      if (setEditorTarget?.id) {
        await onUpdateCaseStudySet(setEditorTarget.id, payload);
      } else {
        await onCreateCaseStudySet(payload);
      }
      closeSetEditor();
    } catch (_error) {
      setSetSubmitting(false);
    }
  };

  const toggleSetCaseStudy = (caseStudyId) => {
    setSetFields((current) => ({
      ...current,
      caseStudyIds: current.caseStudyIds.includes(caseStudyId)
        ? current.caseStudyIds.filter((item) => item !== caseStudyId)
        : [...current.caseStudyIds, caseStudyId],
    }));
  };

  const confirmDeleteSet = async () => {
    if (!caseStudySetDeleteTarget) {
      return;
    }
    setSetSubmitting(true);
    try {
      await onDeleteCaseStudySet(caseStudySetDeleteTarget.id);
      setCaseStudySetDeleteTarget(null);
      setSetSubmitting(false);
    } catch (_error) {
      setSetSubmitting(false);
    }
  };

  const openPublishModal = (caseStudy, mode) => {
    setPublishTarget({ caseStudy, mode });
    setPublishSubmitting(false);
  };

  const closePublishModal = () => {
    setPublishTarget(null);
    setPublishSubmitting(false);
  };

  const submitPublish = async () => {
    if (!publishTarget || !onPublishCaseStudy) {
      return;
    }

    setPublishSubmitting(true);
    try {
      await onPublishCaseStudy(publishTarget.caseStudy.id, publishTarget.mode);
      closePublishModal();
    } catch (_error) {
      setPublishSubmitting(false);
    }
  };

  const submitSetShare = async (event) => {
    event?.preventDefault?.();
    if (!caseStudySetShareTarget) {
      return;
    }
    setCaseStudySetShareSubmitting(true);
    try {
      await onShareCaseStudySet(caseStudySetShareTarget.id, caseStudySetShareEmail.trim());
      setCaseStudySetShareTarget(null);
      setCaseStudySetShareEmail('');
      setCaseStudySetShareSubmitting(false);
    } catch (_error) {
      setCaseStudySetShareSubmitting(false);
    }
  };

  return (
    <>
      <div className="student-page">
        <Container fluid className="mt-4 mb-5 px-4 student-page__content facilitator-library-shell">
          <div className="student-dashboard-shell">
            <div className="student-dashboard-header">
              <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                <div>
                  <h2 className="mb-2">View and share case studies</h2>
                  <p className="student-dashboard-header__copy mb-1">
                    Manage your own case studies, share them with students, and review usage statistics.
                  </p>
                </div>
              </div>
              <Button type="button" variant="outline-secondary" className="btn-sm" onClick={onBack}>
                <i className="bi bi-arrow-left" aria-hidden="true" />{' '}
                Back
              </Button>
              {' '}
              <Button
                type="button"
                variant="light"
                className="btn-sm"
                onClick={() => {
                  setTutorialStep(0);
                  setShowTutorial(true);
                }}
              >
                <i className="bi bi-question-circle" aria-hidden="true" />{' '}
                Tutorial
              </Button>
            </div>

            <div className="student-dashboard-section">
              <div className="student-dashboard-section__header">
                <h4 className="mb-1">My case studies</h4>
                <p className="student-dashboard-header__copy mb-0">
                  {totals.owned} owned case{totals.owned === 1 ? '' : 's'} available.
                </p>
              </div>
              <div
                ref={filterControlsRef}
                className={isTutorialTargetActive('filters') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}
              >
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Group controlId="facilitatorCaseSearch" className="mb-0" style={{ width: '240px' }}>
                    <Form.Control
                      type="search"
                      size="sm"
                      value={caseSearch}
                      onChange={(event) => setCaseSearch(event.target.value)}
                      placeholder="Search case studies"
                    />
                  </Form.Group>
                  <Button
                    type="button"
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setShowFilters(true)}
                  >
                    <i className="bi bi-funnel me-1" aria-hidden="true" />
                    Filters
                    {(statusFilter !== 'all' || tierFilter !== 'all') ? (
                      <Badge bg="primary" className="ms-2">
                        {(statusFilter !== 'all' ? 1 : 0) + (tierFilter !== 'all' ? 1 : 0)}
                      </Badge>
                    ) : null}
                  </Button>
                </div>
                <div className="small text-muted mt-2">
                  Showing {filteredOwnedCaseStudies.length ? ((casePage - 1) * PAGE_SIZE) + 1 : 0}
                  {' '}-{' '}
                  {Math.min(casePage * PAGE_SIZE, filteredOwnedCaseStudies.length)}
                  {' '}of {filteredOwnedCaseStudies.length}
                </div>
              </div>

              <Offcanvas show={showFilters} onHide={() => setShowFilters(false)} placement="end">
                <Offcanvas.Header closeButton>
                  <Offcanvas.Title>Filter case studies</Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                  <div className="mb-4">
                    <div className="small fw-semibold mb-2">Status</div>
                    <div className="d-flex gap-2 flex-wrap">
                      {FILTER_OPTIONS.map((option) => (
                        <Button
                          key={option.value}
                          type="button"
                          size="sm"
                          variant={statusFilter === option.value ? 'primary' : 'outline-secondary'}
                          onClick={() => setStatusFilter(option.value)}
                        >
                          {option.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  {canCurateFreeTier ? (
                    <div className="mb-4">
                      <div className="small fw-semibold mb-2">Tier</div>
                      <div className="d-flex gap-2 flex-wrap">
                        {[
                          { value: 'all', label: 'All' },
                          { value: 'free', label: 'Free tier' },
                          { value: 'paid', label: 'Paid only' },
                        ].map((option) => (
                          <Button
                            key={option.value}
                            type="button"
                            size="sm"
                            variant={tierFilter === option.value ? 'primary' : 'outline-secondary'}
                            onClick={() => setTierFilter(option.value)}
                          >
                            {option.label}
                          </Button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <div className="d-flex gap-2">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setStatusFilter('all');
                        setTierFilter('all');
                      }}
                    >
                      Reset
                    </Button>
                    <Button type="button" variant="primary" size="sm" onClick={() => setShowFilters(false)}>
                      Apply
                    </Button>
                  </div>
                </Offcanvas.Body>
              </Offcanvas>
              {filteredOwnedCaseStudies.length ? (
                <>
                <div
                  ref={caseTableRef}
                  className={`table-responsive ${isTutorialTargetActive('cases') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}
                >
                  <Table borderless hover className="facilitator-library-table facilitator-library-table--cases mb-0">
                    <colgroup>
                      <col className="facilitator-library-col--main" />
                      <col className="facilitator-library-col--meta" />
                      <col className="facilitator-library-col--meta" />
                      <col className="facilitator-library-col--actions" />
                      <col className="facilitator-library-col--publish" />
                      <col className="facilitator-library-col--more" />
                    </colgroup>
                    <thead style={{ color: readableTextOn(tenant?.brandPrimaryColor || '#2563eb') }}>
                      <tr>
                        <th>
                          <button
                            type="button"
                            className="facilitator-library-sort-header"
                            onClick={() => toggleSort('title')}
                            aria-sort={sortKey === 'title' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                          >
                            Case study
                            <i className={`bi ${sortKey === 'title' ? (sortDir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-arrow-down-up'} ms-1`} aria-hidden="true" />
                          </button>
                        </th>
                        <th className="facilitator-library-cell--status">Availability</th>
                        <th className="facilitator-library-cell--updated">
                          <button
                            type="button"
                            className="facilitator-library-sort-header"
                            onClick={() => toggleSort('updatedAt')}
                            aria-sort={sortKey === 'updatedAt' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                          >
                            Updated
                            <i className={`bi ${sortKey === 'updatedAt' ? (sortDir === 'asc' ? 'bi-caret-up-fill' : 'bi-caret-down-fill') : 'bi-arrow-down-up'} ms-1`} aria-hidden="true" />
                          </button>
                        </th>
                        <th>Edit</th>
                        <th>Publish</th>
                        <th className="facilitator-library-cell--more">
                          <span className="visually-hidden">More actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedOwnedCaseStudies.map((caseStudy) => {
                        const ownedByOther = user?.sub && caseStudy.ownerUserId && caseStudy.ownerUserId !== user.sub;
                        const isCoEditor = ownedByOther && user?.role !== 'platform_admin' && user?.role !== 'educator_admin';
                        const studentShares = (caseStudy.shares || []).filter((share) => share.shareType === 'student');
                        const hasSchedule = Boolean(caseStudy.scheduledPublishAt || caseStudy.scheduledUnpublishAt);
                        const hasCohortLimit = cohortVisibilityBadgeCount(caseStudy) > 0;
                        return (
                        <tr key={caseStudy.id}>
                          <td>
                            <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                              <div className="fs-5 fw-semibold text-body lh-sm">{caseStudy.title}</div>
                              {isCoEditor ? <Badge bg="info">Co-editor</Badge> : null}
                            </div>
                            <div className="text-muted mb-1" style={{ fontSize: '0.875rem' }}>
                              {caseStudy.draftData?.short_description || caseStudy.summary || 'No description added yet.'}
                            </div>
                            {ownedByOther ? (
                              <div className="small text-muted">
                                <i className="bi bi-person-badge me-1" />
                                {caseStudy.ownerDisplayName || caseStudy.ownerEmail || 'another author'}
                              </div>
                            ) : null}
                            {caseStudy.scheduledPublishAt && new Date(caseStudy.scheduledPublishAt) > new Date() ? (
                              <div className="small text-warning-emphasis">
                                <i className="bi bi-calendar-event me-1" />
                                Goes live {formatDateTime(caseStudy.scheduledPublishAt)}
                              </div>
                            ) : null}
                            {caseStudy.scheduledUnpublishAt && new Date(caseStudy.scheduledUnpublishAt) > new Date() ? (
                              <div className="small text-warning-emphasis">
                                <i className="bi bi-calendar-x me-1" />
                                Unpublishes {formatDateTime(caseStudy.scheduledUnpublishAt)}
                              </div>
                            ) : null}
                            {hasCohortLimit ? (
                              <div className="small text-warning-emphasis">
                                <i className="bi bi-people me-1" />
                                Limited to {cohortVisibilityBadgeCount(caseStudy)} cohort{cohortVisibilityBadgeCount(caseStudy) === 1 ? '' : 's'}
                              </div>
                            ) : null}
                            {studentShares.length ? (
                              <div className="small text-muted">{getStudentShareSummary(caseStudy)}</div>
                            ) : null}
                          </td>
                          <td className="facilitator-library-cell--status">
                            <CaseStudyStatusCell
                              caseStudy={caseStudy}
                              canApprove={canApprove}
                              onApproveCase={(cs) => openReviewModal(cs, 'approve')}
                              onRejectCase={(cs) => openReviewModal(cs, 'reject')}
                              onOpenReviewThread={(cs) => openReviewModal(cs, 'comment')}
                              reviewCommentCount={reviewCommentBadgeCount(caseStudy)}
                              canCurateFreeTier={canCurateFreeTier}
                              onToggleFreeTier={onToggleFreeTier}
                            />
                          </td>
                          <td className="small facilitator-library-cell--updated">{formatDateTime(caseStudy.updatedAt)}</td>
                          <td>
                            <div
                              ref={casePage === 1 && pagedOwnedCaseStudies[0]?.id === caseStudy.id ? firstActionsRef : null}
                              className={`facilitator-library-actions-row ${isTutorialTargetActive('actions') && casePage === 1 && pagedOwnedCaseStudies[0]?.id === caseStudy.id ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}
                            >
                              <button
                                type="button"
                                className="facilitator-library-action-pill facilitator-library-action-pill--edit"
                                onClick={() => onEditCaseStudy(caseStudy.id)}
                              >
                                <i className="bi bi-pencil-square me-1" aria-hidden="true" /> Edit case
                              </button>
                            </div>
                          </td>
                          <td>
                            {caseStudy.status !== 'archived' ? (() => {
                              const isLiveClassroomActive = caseStudy.status === 'live_classroom';
                              const isCaseStudyPublished = caseStudy.status === 'self_paced' || caseStudy.studentAccessEnabled;
                              const anyPublished = isLiveClassroomActive || isCaseStudyPublished;
                              return (
                                <Dropdown>
                                  <Dropdown.Toggle as="button" type="button" className={anyPublished ? 'facilitator-library-action-pill facilitator-library-action-pill--update' : 'facilitator-library-action-pill facilitator-library-action-pill--publish'}>
                                    <i className={`bi ${anyPublished ? 'bi-arrow-repeat' : 'bi-upload'} me-1`} aria-hidden="true" />
                                    {anyPublished ? 'Update' : 'Publish'}
                                  </Dropdown.Toggle>
                                  <Dropdown.Menu align="end" popperConfig={{ strategy: 'fixed' }} renderOnMount>
                                    <Dropdown.Item onClick={() => openPublishModal(caseStudy, 'self_paced')}>
                                      <i className="bi bi-mortarboard me-2" aria-hidden="true" />
                                      {isCaseStudyPublished ? 'Update case study' : 'Publish as case study'}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => openPublishModal(caseStudy, 'live_classroom')}>
                                      <i className="bi bi-broadcast me-2" aria-hidden="true" />
                                      {isLiveClassroomActive ? 'Update live classroom' : 'Publish as live classroom'}
                                    </Dropdown.Item>
                                  </Dropdown.Menu>
                                </Dropdown>
                              );
                            })() : (
                              <span className="text-muted small">Archived</span>
                            )}
                          </td>
                          <td className="facilitator-library-cell--more">
                            <Dropdown className="facilitator-library-more-wrap">
                              <Dropdown.Toggle as="button" type="button" className="facilitator-library-more-toggle" aria-label="More actions" title="More actions">
                                <i className="bi bi-three-dots-vertical" aria-hidden="true" />
                              </Dropdown.Toggle>
                                <Dropdown.Menu align="end" popperConfig={{ strategy: 'fixed' }} renderOnMount>
                                  <Dropdown.Item onClick={() => onTestCaseStudy(caseStudy.id)}>
                                    <i className="bi bi-play-circle-fill me-2" aria-hidden="true" /> Test
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => onViewResults(caseStudy.id)}>
                                    <i className="bi bi-clipboard-data me-2" aria-hidden="true" /> Results
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  <Dropdown.Item onClick={() => openShareModal(caseStudy)}>
                                    <i className="bi bi-share-fill me-2" aria-hidden="true" /> Share with educator
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => openSharesModal(caseStudy)}>
                                    <i className="bi bi-people-fill me-2" aria-hidden="true" /> Manage student shares
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => openScheduleModal(caseStudy)}>
                                    <i className="bi bi-calendar-event me-2" aria-hidden="true" />
                                    {hasSchedule ? 'Edit publish schedule' : 'Schedule publish'}
                                  </Dropdown.Item>
                                  <Dropdown.Item onClick={() => openVisibilityModal(caseStudy)}>
                                    <i className="bi bi-people me-2" aria-hidden="true" />
                                    {hasCohortLimit ? 'Edit cohort visibility' : 'Limit to cohorts'}
                                  </Dropdown.Item>
                                  {(caseStudy.status === 'self_paced' || caseStudy.studentAccessEnabled) ? (
                                    <Dropdown.Item onClick={() => onCopyStudentLink(caseStudy.id)}>
                                      <i className="bi bi-link-45deg me-2" aria-hidden="true" /> Copy student link
                                    </Dropdown.Item>
                                  ) : null}
                                  {caseStudy.status === 'live_classroom' && caseStudy.activeSessionCode ? (
                                    <>
                                      <Dropdown.Item onClick={() => onPresentCaseStudy(caseStudy.id)}>
                                        <i className="bi bi-easel-fill me-2" aria-hidden="true" /> Present live case
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => onCopyPresentationLink(caseStudy.id)}>
                                        <i className="bi bi-link-45deg me-2" aria-hidden="true" /> Copy presentation link
                                      </Dropdown.Item>
                                      <Dropdown.Item onClick={() => onEndLiveClassroom(caseStudy.id)}>
                                        <i className="bi bi-stop-circle-fill me-2" aria-hidden="true" /> End live session
                                      </Dropdown.Item>
                                    </>
                                  ) : null}
                                  <Dropdown.Item onClick={() => openHistoryModal(caseStudy)} disabled={historyLoadingId === caseStudy.id}>
                                    <i className="bi bi-clock-history me-2" aria-hidden="true" />
                                    {historyLoadingId === caseStudy.id
                                      ? 'Loading history…'
                                      : caseStudy.recentAccesses?.length
                                        ? `View history (${caseStudy.recentAccesses.length})`
                                        : 'View history'}
                                  </Dropdown.Item>
                                  <Dropdown.Divider />
                                  {caseStudy.status !== 'archived' ? (
                                    <Dropdown.Item onClick={() => onArchiveCaseStudy(caseStudy.id)}>
                                      <i className="bi bi-archive-fill me-2" aria-hidden="true" /> Archive
                                    </Dropdown.Item>
                                  ) : (
                                    <Dropdown.Item onClick={() => onUnarchiveCaseStudy(caseStudy.id)}>
                                      <i className="bi bi-arrow-counterclockwise me-2" aria-hidden="true" /> Restore from archive
                                    </Dropdown.Item>
                                  )}
                                  <Dropdown.Item className="text-danger" onClick={() => openDeleteModal(caseStudy)}>
                                    <i className="bi bi-trash-fill me-2" aria-hidden="true" /> Delete
                                  </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                {casePageCount > 1 ? (
                  <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-3">
                    <div className="small text-muted">Page {casePage} of {casePageCount}</div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button type="button" size="sm" variant="outline-secondary" onClick={() => setCasePage((current) => Math.max(1, current - 1))} disabled={casePage === 1}>
                        Previous
                      </Button>
                      <Button type="button" size="sm" variant="outline-secondary" onClick={() => setCasePage((current) => Math.min(casePageCount, current + 1))} disabled={casePage === casePageCount}>
                        Next
                      </Button>
                    </div>
                  </div>
                ) : null}
                </>
              ) : (
                <div className="student-dashboard-empty">
                  <i className="bi bi-folder2-open" aria-hidden="true" />
                  {ownedCaseStudies.length ? (
                    <>
                      <div className="mb-2">No case studies match your filters or search.</div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => {
                          setStatusFilter('all');
                          setTierFilter('all');
                          setCaseSearch('');
                        }}
                      >
                        Clear filters
                      </Button>
                    </>
                  ) : (
                    <div>No case studies in your workspace yet.</div>
                  )}
                </div>
              )}
            </div>

            <div
              ref={setsSectionRef}
              className={`student-dashboard-section ${isTutorialTargetActive('sets') ? 'epma-tutorial-target epma-tutorial-target--active' : ''}`}
            >
              <div className="student-dashboard-section__header">
                <h4 className="mb-1">Case study sets</h4>
                <p className="student-dashboard-header__copy mb-0">
                  Group non-live case studies into sets and share the whole set with students.
                </p>
              </div>
              <div className="d-flex justify-content-start mb-3">
                <Button type="button" variant="primary" onClick={() => openSetEditor()}>
                  Create case study set
                </Button>
              </div>
              <div className="d-flex justify-content-between align-items-end gap-3 flex-wrap">
                <Form.Group controlId="facilitatorSetSearch" className="mb-0" style={{ minWidth: '280px', flex: '1 1 320px' }}>
                  <Form.Label className="small text-muted mb-1">Search case study sets</Form.Label>
                  <Form.Control
                    type="search"
                    value={setSearch}
                    onChange={(event) => setSetSearch(event.target.value)}
                    placeholder="Search by set title, description, or included case"
                  />
                </Form.Group>
                <div className="small text-muted">
                  Showing {filteredCaseStudySets.length ? ((setPage - 1) * PAGE_SIZE) + 1 : 0}
                  {' '}-{' '}
                  {Math.min(setPage * PAGE_SIZE, filteredCaseStudySets.length)}
                  {' '}of {filteredCaseStudySets.length}
                </div>
              </div>
              {filteredCaseStudySets.length ? (
                <>
                <div className="table-responsive">
                  <Table borderless hover className="facilitator-library-table facilitator-library-table--sets mb-0">
                    <thead style={{ color: readableTextOn(tenant?.brandPrimaryColor || '#2563eb') }}>
                      <tr>
                        <th>Set</th>
                        <th>Included cases</th>
                        <th>Edit</th>
                        <th className="facilitator-library-cell--more">
                          <span className="visually-hidden">More actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagedCaseStudySets.map((caseStudySet) => (
                        <tr key={caseStudySet.id}>
                          <td>
                            <div className="fs-5 fw-semibold text-body lh-sm mb-1">{caseStudySet.title}</div>
                            <div className="text-muted" style={{ fontSize: '0.875rem' }}>{caseStudySet.description || 'No description added yet.'}</div>
                          </td>
                          <td className="small">{(caseStudySet.caseStudies || []).map((item) => item.title).join(', ') || 'No cases added yet.'}</td>
                          <td>
                            <button
                              type="button"
                              className="facilitator-library-action-pill facilitator-library-action-pill--edit"
                              onClick={() => openSetEditor(caseStudySet)}
                            >
                              <i className="bi bi-pencil-square me-1" aria-hidden="true" /> Edit set
                            </button>
                          </td>
                          <td className="facilitator-library-cell--more">
                            <Dropdown className="facilitator-library-more-wrap">
                              <Dropdown.Toggle as="button" type="button" className="facilitator-library-more-toggle" aria-label="More actions" title="More actions">
                                <i className="bi bi-three-dots-vertical" aria-hidden="true" />
                              </Dropdown.Toggle>
                              <Dropdown.Menu align="end">
                                <Dropdown.Item onClick={() => { setCaseStudySetShareTarget(caseStudySet); setCaseStudySetShareEmail(''); }}>
                                  <i className="bi bi-share-fill me-2" aria-hidden="true" /> Share set
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item className="text-danger" onClick={() => setCaseStudySetDeleteTarget(caseStudySet)}>
                                  <i className="bi bi-trash-fill me-2" aria-hidden="true" /> Delete set
                                </Dropdown.Item>
                              </Dropdown.Menu>
                            </Dropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {setPageCount > 1 ? (
                  <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap mt-3">
                    <div className="small text-muted">Page {setPage} of {setPageCount}</div>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button type="button" size="sm" variant="outline-secondary" onClick={() => setSetPage((current) => Math.max(1, current - 1))} disabled={setPage === 1}>
                        Previous
                      </Button>
                      <Button type="button" size="sm" variant="outline-secondary" onClick={() => setSetPage((current) => Math.min(setPageCount, current + 1))} disabled={setPage === setPageCount}>
                        Next
                      </Button>
                    </div>
                  </div>
                ) : null}
                </>
              ) : (
                <div className="student-dashboard-empty">
                  <i className="bi bi-collection" aria-hidden="true" />
                  <div>{caseStudySets.length ? 'No case study sets match this search.' : 'No case study sets yet.'}</div>
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>

      {showTutorial ? (
        <div className="epma-tutorial-overlay" role="dialog" aria-modal="true" aria-labelledby="caseLibraryTutorialTitle">
          <div className="epma-tutorial-overlay__backdrop" onClick={closeTutorial} />
          <Card className="epma-tutorial-card container-shadow" style={tutorialCardStyle}>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <div>
                  <div className="epma-tutorial-card__eyebrow">
                    Step {tutorialStep + 1} of {tutorialSteps.length}
                  </div>
                  <h4 id="caseLibraryTutorialTitle" className="mb-1">{tutorialSteps[tutorialStep]?.title}</h4>
                </div>
                <button type="button" className="epma-close-button" onClick={closeTutorial} aria-label="Skip tutorial">
                  <i className="bi bi-x-lg" aria-hidden="true" />
                </button>
              </div>
              <p className="mb-0">{tutorialSteps[tutorialStep]?.body}</p>
              <div className="epma-tutorial-card__actions">
                <Button type="button" variant="outline-secondary" onClick={closeTutorial}>
                  Skip
                </Button>
                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={() => setTutorialStep((current) => Math.max(0, current - 1))}
                    disabled={tutorialStep === 0}
                  >
                    Back
                  </Button>
                  <Button
                    type="button"
                    onClick={() => {
                      if (tutorialStep === tutorialSteps.length - 1) {
                        closeTutorial();
                        return;
                      }
                      setTutorialStep((current) => current + 1);
                    }}
                  >
                    {tutorialStep === tutorialSteps.length - 1 ? 'Finish' : 'Next'}
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>
      ) : null}

      <Modal show={Boolean(shareTarget)} onHide={closeShareModal}>
        <Modal.Header closeButton>
          <Modal.Title>Share case study</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitShare}>
            <div className="mb-3">
              <div className="fw-semibold">{shareTarget?.title}</div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Share type</Form.Label>
              <div>
                <Form.Check
                  type="radio"
                  id="share-type-student"
                  name="shareType"
                  label="Give a student direct access to this case"
                  checked={shareType === 'student'}
                  onChange={() => setShareType('student')}
                />
                <Form.Check
                  type="radio"
                  id="share-type-editor"
                  name="shareType"
                  label="Add a co-editor (another educator who can edit this case)"
                  checked={shareType === 'editor'}
                  onChange={() => setShareType('editor')}
                />
              </div>
            </Form.Group>
            <Form.Group className="mb-3" controlId="shareCaseEmail">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                value={shareEmail}
                onChange={(event) => setShareEmail(event.target.value)}
                placeholder="name@example.com"
              />
            </Form.Group>
            <Alert variant="info" className="mb-3">
              {shareType === 'editor'
                ? 'The recipient must already have an educator account on this tenant. They will be able to edit the draft and publish, but cannot delete or transfer ownership of the case.'
                : 'Students still need to be logged in, and the case must have student access turned on to open from the shared link.'}
            </Alert>
            {shareError ? <Alert variant="danger">{shareError}</Alert> : null}
            <div className="d-flex justify-content-end gap-2">
              <Button type="button" variant="secondary" onClick={closeShareModal}>Cancel</Button>
              <Button type="submit" disabled={!shareEmail.trim() || shareSubmitting}>
                {shareSubmitting ? 'Sharing...' : (shareType === 'editor' ? 'Add co-editor' : 'Share case study')}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(deleteTarget)} onHide={closeDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>Delete case study</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Delete <strong>{deleteTarget?.title}</strong> from your workspace?</p>
          <div className="small text-muted">
            This cannot be undone.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={closeDeleteModal}>Cancel</Button>
          <Button type="button" variant="danger" onClick={confirmDelete} disabled={deleteSubmitting}>
            {deleteSubmitting ? 'Deleting...' : 'Confirm delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(historyTarget)} onHide={closeHistoryModal}>
        <Modal.Header closeButton>
          <Modal.Title>Student access history</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="fw-semibold mb-3">{historyTarget?.title}</div>
          {historyTarget?.recentAccesses?.length ? (
            <div className="facilitator-library-access-list">
              {historyTarget.recentAccesses.map((attempt, index) => (
                <div key={`${attempt.learnerEmail}-${attempt.updatedAt}-${index}`} className="facilitator-library-access-list__item">
                  <div className="fw-semibold">{attempt.learnerName || attempt.learnerEmail}</div>
                  <div className="small text-muted">{attempt.learnerEmail}</div>
                  <div className="small text-muted">
                    {attempt.accessType === 'live_session'
                      ? `Viewed live session${attempt.sessionCode ? ` ${attempt.sessionCode}` : ''}`
                      : 'Opened self-paced case'}{' '}
                    {formatDateTime(attempt.startedAt)}
                  </div>
                  <div className="small text-muted">Last activity {formatDateTime(attempt.updatedAt)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted">No student access recorded yet.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={closeHistoryModal}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(sharesTarget)} onHide={closeSharesModal}>
        <Modal.Header closeButton>
          <Modal.Title>Manage shares</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="fw-semibold mb-3">{sharesTarget?.title}</div>

          <div className="small fw-semibold text-uppercase text-muted mb-2">Co-editors</div>
          {sharesTarget?.shares?.filter((share) => share.shareType === 'editor').length ? (
            <div className="facilitator-library-access-list mb-3">
              {sharesTarget.shares.filter((share) => share.shareType === 'editor').map((share) => (
                <div key={share.id} className="facilitator-library-access-list__item">
                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div className="fw-semibold">{share.recipientName || share.recipientEmail}</div>
                      <div className="small text-muted">{share.recipientEmail}</div>
                      <div className="small text-muted">Added {formatDateTime(share.sharedAt)}</div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="danger"
                      onClick={() => revokeShare(sharesTarget.id, share.id)}
                      disabled={revokeSubmitting === share.id}
                    >
                      {revokeSubmitting === share.id ? 'Removing...' : 'Remove'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted mb-3">No co-editors yet.</div>
          )}

          <div className="small fw-semibold text-uppercase text-muted mb-2">Direct student shares</div>
          {sharesTarget?.shares?.filter((share) => share.shareType === 'student').length ? (
            <div className="facilitator-library-access-list">
              {sharesTarget.shares.filter((share) => share.shareType === 'student').map((share) => (
                <div key={share.id} className="facilitator-library-access-list__item">
                  <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                    <div>
                      <div className="fw-semibold">{share.recipientName || share.recipientEmail}</div>
                      <div className="small text-muted">{share.recipientEmail}</div>
                      <div className="small text-muted">Shared {formatDateTime(share.sharedAt)}</div>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="danger"
                      onClick={() => revokeShare(sharesTarget.id, share.id)}
                      disabled={revokeSubmitting === share.id}
                    >
                      {revokeSubmitting === share.id ? 'Removing...' : 'Remove'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-muted">No direct student shares yet.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={closeSharesModal}>Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(reviewTarget)} onHide={closeReviewModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {reviewMode === 'reject' ? 'Send case back to author' : reviewMode === 'approve' ? 'Approve case' : 'Review thread'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="fw-semibold mb-3">{reviewTarget?.title}</div>
          {reviewError ? <Alert variant="danger">{reviewError}</Alert> : null}
          {reviewLoading ? (
            <div className="text-muted">Loading thread…</div>
          ) : (
            <div className="d-flex flex-column gap-2 mb-3" style={{ maxHeight: 320, overflowY: 'auto' }}>
              {reviewComments.length === 0 ? (
                <div className="text-muted small">No comments yet.</div>
              ) : (
                reviewComments.map((comment) => {
                  const kindBadge = comment.kind === 'rejection'
                    ? { bg: 'danger', label: 'Rejection' }
                    : comment.kind === 'approval'
                      ? { bg: 'success', label: 'Approval' }
                      : null;
                  const roleLabel = comment.authorRole === 'educator_admin'
                    ? 'Facilitator admin'
                    : comment.authorRole === 'platform_admin'
                      ? 'Platform admin'
                      : 'Educator';
                  const canDelete = comment.authorUserId === user?.id || user?.role === 'educator_admin' || user?.role === 'platform_admin';
                  return (
                    <div key={comment.id} className="border rounded p-2">
                      <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                        <div className="small">
                          <strong>{comment.authorDisplayName || comment.authorEmail || 'Unknown'}</strong>
                          <span className="text-muted ms-2">{roleLabel}</span>
                          {kindBadge ? <Badge bg={kindBadge.bg} className="ms-2">{kindBadge.label}</Badge> : null}
                        </div>
                        <div className="small text-muted">{formatDateTime(comment.createdAt)}</div>
                      </div>
                      {comment.body ? (
                        <div style={{ whiteSpace: 'pre-wrap' }}>{comment.body}</div>
                      ) : (
                        <div className="text-muted small fst-italic">No message.</div>
                      )}
                      {canDelete ? (
                        <div className="mt-1">
                          <Button type="button" variant="link" size="sm" className="p-0 text-danger" onClick={() => deleteReviewComment(comment.id)}>
                            Delete
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          )}
          <Form.Group>
            <Form.Label className="small fw-semibold">
              {reviewMode === 'reject'
                ? 'What does the author need to change?'
                : reviewMode === 'approve'
                  ? 'Optional note for the author'
                  : 'Add a comment'}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={reviewBody}
              onChange={(event) => setReviewBody(event.target.value)}
              placeholder={reviewMode === 'reject' ? 'Explain why the case needs more work…' : 'Type a message…'}
              disabled={reviewSubmitting}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between flex-wrap gap-2">
          <Button type="button" variant="secondary" onClick={closeReviewModal} disabled={reviewSubmitting}>
            Close
          </Button>
          <div className="d-flex gap-2 flex-wrap">
            {reviewMode === 'reject' ? (
              <Button type="button" variant="outline-danger" onClick={submitReviewReject} disabled={reviewSubmitting}>
                {reviewSubmitting ? 'Sending…' : 'Send back to author'}
              </Button>
            ) : reviewMode === 'approve' ? (
              <Button type="button" variant="success" onClick={submitReviewApprove} disabled={reviewSubmitting}>
                {reviewSubmitting ? 'Approving…' : 'Approve case'}
              </Button>
            ) : (
              <Button type="button" variant="primary" onClick={submitReviewComment} disabled={reviewSubmitting || !reviewBody.trim()}>
                {reviewSubmitting ? 'Posting…' : 'Post comment'}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(visibilityTarget)} onHide={closeVisibilityModal}>
        <Form onSubmit={submitVisibility}>
          <Modal.Header closeButton>
            <Modal.Title>Cohort visibility</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="fw-semibold mb-2">{visibilityTarget?.title}</div>
            <Alert variant="info" className="mb-3">
              Leave all cohorts unticked to keep this case visible to every student in your tenant. Tick one or more cohorts to restrict it to those students only.
            </Alert>
            {visibilityError ? <Alert variant="danger">{visibilityError}</Alert> : null}
            {visibilityLoading ? (
              <div className="text-muted">Loading cohorts…</div>
            ) : visibilityCohorts.length === 0 ? (
              <Alert variant="secondary" className="mb-0">
                No cohorts have been created yet. An educator admin can add them under <strong>Admin → Cohorts</strong>.
              </Alert>
            ) : (
              <div className="d-flex flex-column gap-2">
                {visibilityCohorts.map((cohort) => (
                  <Form.Check
                    key={cohort.id}
                    type="checkbox"
                    id={`visibility-cohort-${cohort.id}`}
                    label={`${cohort.name}${cohort.memberCount ? ` (${cohort.memberCount} member${cohort.memberCount === 1 ? '' : 's'})` : ''}`}
                    checked={visibilitySelectedIds.includes(cohort.id)}
                    onChange={() => toggleVisibilitySelection(cohort.id)}
                  />
                ))}
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" variant="secondary" onClick={closeVisibilityModal} disabled={visibilitySaving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={visibilityLoading || visibilitySaving}>
              {visibilitySaving ? 'Saving…' : 'Save visibility'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={Boolean(scheduleTarget)} onHide={closeScheduleModal}>
        <Modal.Header closeButton>
          <Modal.Title>Schedule publish</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitSchedule}>
            <div className="fw-semibold mb-3">{scheduleTarget?.title}</div>
            <Alert variant="info" className="mb-3">
              Cases auto-flip to student-accessible at the publish time and auto-hide at the unpublish time. Leave a field blank to skip that side. Times use your local timezone.
            </Alert>
            <Form.Group className="mb-3">
              <Form.Label>Publish at (optional)</Form.Label>
              <Form.Control
                type="datetime-local"
                value={scheduleForm.publishAt}
                onChange={(event) => setScheduleForm((current) => ({ ...current, publishAt: event.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Unpublish at (optional)</Form.Label>
              <Form.Control
                type="datetime-local"
                value={scheduleForm.unpublishAt}
                onChange={(event) => setScheduleForm((current) => ({ ...current, unpublishAt: event.target.value }))}
              />
            </Form.Group>
            {scheduleError ? <Alert variant="danger">{scheduleError}</Alert> : null}
            <div className="d-flex justify-content-between gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-danger"
                onClick={clearSchedule}
                disabled={scheduleSubmitting || (!scheduleTarget?.scheduledPublishAt && !scheduleTarget?.scheduledUnpublishAt)}
              >
                Clear schedule
              </Button>
              <div className="d-flex gap-2">
                <Button type="button" variant="secondary" onClick={closeScheduleModal} disabled={scheduleSubmitting}>Cancel</Button>
                <Button type="submit" variant="primary" disabled={scheduleSubmitting || (!scheduleForm.publishAt && !scheduleForm.unpublishAt)}>
                  {scheduleSubmitting ? 'Saving...' : 'Save schedule'}
                </Button>
              </div>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={Boolean(publishTarget)} onHide={closePublishModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            {publishTarget?.mode === 'live_classroom' ? 'Start live classroom' : 'Publish self-paced'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {publishTarget ? (() => {
            const draft = getCaseStudyDraft(publishTarget.caseStudy);
            const validation = getCaseStudyPublishValidation(draft);
            const stagedNeedsLiveControl = hasManualStageProgression(draft) && publishTarget.mode !== 'live_classroom';
            const isQuestionTriggeredStagedCase = hasStagedLiveCase(draft) && !hasManualStageProgression(draft);
            return (
              <>
                <div className="mb-3">
                  <div className="fw-semibold">{publishTarget.caseStudy.title}</div>
                  <div className="small text-muted">
                    {publishTarget.mode === 'live_classroom'
                      ? 'Students will join with a live classroom code.'
                      : 'Students will be able to open the published version from shared links.'}
                  </div>
                </div>
                <div className="facilitator-validation-list">
                  {validation.requiredChecks.map((check) => (
                    <div
                      key={check.key}
                      className={`facilitator-validation-list__item${check.passed ? ' facilitator-validation-list__item--pass' : ' facilitator-validation-list__item--fail'}`}
                    >
                      <div className="fw-semibold">{check.label}</div>
                      <div className="small">{check.passed ? 'Ready' : 'Still needed'}</div>
                    </div>
                  ))}
                </div>
                {stagedNeedsLiveControl ? (
                  <Alert variant="warning" className="mt-3 mb-0">
                    This staged case includes manually advanced stages, so it can only be started as a live classroom session. To publish it as a normal case study, set each later stage to trigger after a question is answered.
                  </Alert>
                ) : !validation.ready ? (
                  <Alert variant="warning" className="mt-3 mb-0">
                    Complete the required items in the editor before publishing this case.
                  </Alert>
                ) : isQuestionTriggeredStagedCase && publishTarget.mode !== 'live_classroom' ? (
                  <Alert variant="info" className="mt-3 mb-0">
                    This staged case will publish as a normal case study. Students will move through the stages automatically after answering the configured trigger questions.
                  </Alert>
                ) : (
                  <Alert variant="info" className="mt-3 mb-0">
                    Publishing uses the latest saved draft. Unsaved editor changes will not be included.
                  </Alert>
                )}
              </>
            );
          })() : null}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={closePublishModal}>Cancel</Button>
          <Button
            type="button"
            variant={publishTarget?.mode === 'live_classroom' ? 'success' : 'info'}
            onClick={submitPublish}
            disabled={publishSubmitting || (publishTarget ? (
              !getCaseStudyPublishValidation(getCaseStudyDraft(publishTarget.caseStudy)).ready
              || (hasManualStageProgression(getCaseStudyDraft(publishTarget.caseStudy)) && publishTarget.mode !== 'live_classroom')
            ) : true)}
          >
            {publishSubmitting
              ? 'Publishing...'
              : publishTarget?.mode === 'live_classroom'
                ? 'Start live classroom'
                : 'Publish self-paced'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showSetEditor} onHide={closeSetEditor} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{setEditorTarget ? 'Edit case study set' : 'Create case study set'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitSetEditor}>
            <Form.Group className="mb-3" controlId="caseStudySetTitle">
              <Form.Label>Set title</Form.Label>
              <Form.Control
                type="text"
                value={setFields.title}
                onChange={(event) => setSetFields((current) => ({ ...current, title: event.target.value }))}
                placeholder="e.g. Cardiology revision set"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="caseStudySetDescription">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={setFields.description}
                onChange={(event) => setSetFields((current) => ({ ...current, description: event.target.value }))}
                placeholder="Optional short summary for students"
              />
            </Form.Group>
            <Form.Group controlId="caseStudySetCases">
              <Form.Label>Include case studies</Form.Label>
              <div className="border rounded p-3 facilitator-library-set-list">
                {eligibleSetCaseStudies.length ? eligibleSetCaseStudies.map((caseStudy) => (
                  <Form.Check
                    key={caseStudy.id}
                    type="checkbox"
                    id={`set-case-${caseStudy.id}`}
                    className="mb-2"
                    label={`${caseStudy.title} (${caseStudy.status === 'self_paced' || caseStudy.studentAccessEnabled ? 'live case study' : 'not live'})`}
                    checked={setFields.caseStudyIds.includes(caseStudy.id)}
                    onChange={() => toggleSetCaseStudy(caseStudy.id)}
                  />
                )) : <div className="text-muted small">No eligible case studies available.</div>}
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={closeSetEditor}>Cancel</Button>
          <Button type="button" onClick={submitSetEditor} disabled={!setFields.title.trim() || setSubmitting}>
            {setSubmitting ? 'Saving...' : 'Save set'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(caseStudySetDeleteTarget)} onHide={() => setCaseStudySetDeleteTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete case study set</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Delete <strong>{caseStudySetDeleteTarget?.title}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={() => setCaseStudySetDeleteTarget(null)}>Cancel</Button>
          <Button type="button" variant="danger" onClick={confirmDeleteSet} disabled={setSubmitting}>
            {setSubmitting ? 'Deleting...' : 'Delete set'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={Boolean(caseStudySetShareTarget)} onHide={() => setCaseStudySetShareTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Share case study set</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={submitSetShare}>
            <div className="mb-3">
              <div className="fw-semibold">{caseStudySetShareTarget?.title}</div>
              <div className="small text-muted">Share this whole set with a student by email.</div>
            </div>
            <Form.Group controlId="caseStudySetShareEmail">
              <Form.Label>Email address</Form.Label>
              <Form.Control
                type="email"
                value={caseStudySetShareEmail}
                onChange={(event) => setCaseStudySetShareEmail(event.target.value)}
                placeholder="name@example.com"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={() => setCaseStudySetShareTarget(null)}>Cancel</Button>
          <Button type="button" onClick={submitSetShare} disabled={!caseStudySetShareEmail.trim() || caseStudySetShareSubmitting}>
            {caseStudySetShareSubmitting ? 'Sharing...' : 'Share set'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default FacilitatorCaseLibrary;
